import type { UserSelectDto } from '@@types/api.types';
import { ExpertProfileViewModal } from '@components/pages/expert-profile/components/expert-profile-view-modal.component';
import { Avatar } from '@components/shared/avatar.component';
import { cn } from '@lib/utils';
import { type VariantProps, cva } from 'class-variance-authority';
import { User } from 'lucide-react';
import { FC, useState } from 'react';

const expertProfileButtonVariants = cva('flex items-center min-w-0', {
  variants: {
    size: {
      xs: 'gap-2',
      sm: 'gap-2.5',
      md: 'gap-3',
      lg: 'gap-4',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const titleVariants = cva('font-normal truncate', {
  variants: {
    variant: {
      xs: 'text-xs leading-3',
      sm: 'text-sm leading-4',
      md: 'text-base leading-4',
      lg: 'text-lg leading-5',
    },
  },
  defaultVariants: {
    variant: 'md',
  },
});

const subtitleVariants = cva('truncate flex items-center min-w-0', {
  variants: {
    variant: {
      xs: 'text-[10px] gap-1',
      sm: 'text-xs gap-1.5',
      md: 'text-xs gap-1.5',
      lg: 'text-sm gap-2',
    },
  },
  defaultVariants: {
    variant: 'md',
  },
});

const textContainerVariants = cva('flex flex-col min-w-0 overflow-hidden', {
  variants: {
    variant: {
      xs: 'gap-0.5',
      sm: 'gap-0.5',
      md: 'gap-1',
      lg: 'gap-1.5',
    },
  },
  defaultVariants: {
    variant: 'md',
  },
});

type ExpertProfileButtonProps = {
  className?: string;
  expert: UserSelectDto;
  size?: VariantProps<typeof expertProfileButtonVariants>['size'];
};

function getExpertDisplayInfo(expert: UserSelectDto): {
  name: string;
  title?: string;
} {
  const firstName = expert.profile?.firstName;
  const lastName = expert.profile?.lastName;
  const fullName = expert.profile?.fullName;

  const name = fullName ?? (firstName ? `${firstName}${lastName ? ` ${lastName}` : ''}` : 'Expert');
  const title = expert.profile?.title;

  return { name, title };
}

function getAvatarSize(size: ExpertProfileButtonProps['size']): 'lg' | 'md' | 'sm' {
  switch (size) {
    case 'xs':
    case 'sm':
      return 'md';
    case 'md':
      return 'md';
    case 'lg':
      return 'lg';
    default:
      return 'md';
  }
}

function getIconSize(size: ExpertProfileButtonProps['size']): string {
  switch (size) {
    case 'xs':
      return 'h-2 w-2';
    case 'sm':
      return 'h-2.5 w-2.5';
    case 'md':
      return 'h-3 w-3';
    case 'lg':
      return 'h-4 w-4';
    default:
      return 'h-3 w-3';
  }
}

function getAvatarItemVariant(size: ExpertProfileButtonProps['size']): 'lg' | 'md' | 'sm' | 'xs' {
  switch (size) {
    case 'xs':
      return 'xs';
    case 'sm':
      return 'sm';
    case 'md':
      return 'md';
    case 'lg':
      return 'lg';
    default:
      return 'md';
  }
}

export const ExpertProfileButton: FC<ExpertProfileButtonProps> = (props) => {
  const { className, expert, size } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { name, title } = getExpertDisplayInfo(expert);

  const avatarSize = getAvatarSize(size);
  const iconSize = getIconSize(size);
  const avatarItemVariant = getAvatarItemVariant(size);

  const handleOpenModal = (): void => {
    setIsModalOpen(true);
  };

  return (
    <>
      <div className={cn(expertProfileButtonVariants({ size }), className)}>
        <button
          className="shrink-0 cursor-pointer rounded-full transition-opacity hover:opacity-80"
          onClick={handleOpenModal}
          type="button"
        >
          <Avatar
            altText={name}
            imageUrl={expert.avatarUrl ?? undefined}
            initialText={name}
            size={avatarSize}
          />
        </button>
        <div className={textContainerVariants({ variant: avatarItemVariant })}>
          <button
            className={cn(
              titleVariants({ variant: avatarItemVariant }),
              'text-left text-gray-900 cursor-pointer hover:underline transition-colors hover:text-gray-700',
            )}
            onClick={handleOpenModal}
            type="button"
          >
            {name}
          </button>
          {title && (
            <div className={cn(subtitleVariants({ variant: avatarItemVariant }), 'text-[#1F1C1AB2]')}>
              {!expert.avatarUrl && <User className={cn(iconSize, 'text-gray-400')} />}
              <span className="truncate">{title}</span>
            </div>
          )}
        </div>
      </div>

      <ExpertProfileViewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        user={expert}
      />
    </>
  );
};
